import React from 'react';
import { Box, IconButton, type Theme } from '@mui/material';
import type { SxProps } from '@mui/system';
import { InfoRounded, WarningRounded, CloseRounded } from '@mui/icons-material';

// Design-system notification banner (from the shared SVG spec): a coloured
// accent bar with a white icon, a tinted body with a hairline border, the
// message, and an optional close (✕). Three variants — info (blue), warning
// (amber) and error (red) — reused wherever the app needs to surface a
// non-blocking status so they all read as one family.

type Variant = 'info' | 'warning' | 'error';

const TOKENS: Record<
	Variant,
	{ accent: string; tint: string; border: string; Icon: typeof InfoRounded }
> = {
	// Solid (opaque) tints — a semi-transparent fill let the grey page show
	// through and read muddy. These are the SVG tints pre-blended over white.
	info: {
		accent: '#0696D7',
		tint: '#E7F4FB',
		border: '#9BD5EF',
		Icon: InfoRounded,
	},
	warning: {
		accent: '#FAA21B',
		tint: '#FDF3E1',
		border: '#FCC776',
		Icon: WarningRounded,
	},
	error: {
		accent: '#DD2222',
		tint: '#FBE3E3',
		border: '#F9B4B4',
		Icon: WarningRounded,
	},
};

interface Props {
	variant?: Variant;
	children: React.ReactNode;
	onClose?: () => void;
	/** Extra styling on the outer container (e.g. margins). */
	sx?: SxProps<Theme>;
	dataCy?: string;
}

export const NotificationBanner = ({
	variant = 'info',
	children,
	onClose,
	sx,
	dataCy,
}: Props) => {
	const { accent, tint, border, Icon } = TOKENS[variant];

	return (
		<Box
			data-cy={dataCy}
			sx={{
				display: 'flex',
				alignItems: 'stretch',
				border: `1px solid ${border}`,
				background: tint,
				overflow: 'hidden',
				...sx,
			}}>
			<Box
				sx={{
					flex: 'none',
					width: 32,
					background: accent,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
				<Icon sx={{ fontSize: 18, color: '#fff' }} />
			</Box>
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					px: 1.5,
					py: 1,
					fontSize: 13,
					lineHeight: 1.45,
					color: '#3C3C3C',
				}}>
				<Box sx={{ flex: 1 }}>{children}</Box>
				{onClose && (
					<IconButton
						size="small"
						aria-label="Dismiss"
						onClick={onClose}
						sx={{
							flex: 'none',
							color: 'rgba(102, 102, 102, 0.7)',
						}}>
						<CloseRounded fontSize="small" />
					</IconButton>
				)}
			</Box>
		</Box>
	);
};

export default NotificationBanner;
